// ============================================================
// FILE: _services/fetal-doppler/fetal-doppler-manager.service.ts
//
// Ported from EzovionOPDFrontend (Angular 20) core/services/keyar-echo/
// keyar-echo-manager.service.ts to Angular 9, following the same
// translation already applied to x6-manager.service.ts/scope-manager
// .service.ts in this app: @Injectable({providedIn:'root'}) is unchanged
// (source already used the decorator form here, not inject()), and the
// FetalDopplerState type now lives in the shared device-integration model
// file (@core/interfaces/device-integration/device-item.model.ts) instead
// of being declared inline. Renamed KeyarEchoManagerService/State ->
// FetalDopplerManagerService/State per the confirmed migration decision to
// keep this app's existing "Fetal Doppler" naming. The Web Bluetooth
// connection logic, candidate service UUIDs and GATT handling below are
// otherwise unchanged -- do not "clean up" without re-verifying against
// real hardware, per x6-manager.service.ts's own doc comment on this point.
// ============================================================
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  encodeFhrVolumeCommand,
  encodeFhrAlarmValueCommand,
  encodeFhrAlarmLevelCommand,
  encodeTocoResetCommand,
  parseFetalDopplerFrame,
  FetalDopplerFrameReassembler,
  FetalDopplerAudioDecoder,
  FetalDopplerReading,
  FETAL_DOPPLER_AUDIO_SAMPLE_RATE_HZ,
  NOTIFY_CHARACTERISTIC_UUID_PRIMARY,
  NOTIFY_CHARACTERISTIC_UUID_SECONDARY,
  WRITE_CHARACTERISTIC_UUID,
  pcmToWavBlob
} from './fetal-doppler-protocol';
import { FetalDopplerState } from '../../@core/interfaces/device-integration/device-item.model';

// Both the write commands and the read protocol are implemented against
// real, provided sources: the four write commands from disassembling the
// vendor's compiled iOS SDK (see fetal-doppler-protocol.ts's header), and
// the full read/reassembly protocol from Keyar_Echo_BLE_Integration_
// Reference.docx (UUIDs + 9 frame types + circular-buffer reassembly --
// see that file's "READ protocol" section for the implementation).
//
// One real gap remains, specific to the Web platform only (Android/iOS do
// not have it): the vendor's own SDK uses no dedicated service UUID at all
// -- it iterates every service the device exposes and matches directly on
// characteristic UUIDs. Web Bluetooth has no equivalent "iterate
// everything" mode; GATT access must be granted to specific SERVICE UUIDs
// up front via requestDevice()'s optionalServices, before any device is
// even picked. CANDIDATE_SERVICE_UUIDS below is a best-effort list, not a
// confirmed value: 0xFFF1 (secondary notify) / 0xFFF2 (write) match the
// extremely common "0xFFF0 custom service with 0xFFF1-0xFFF4
// characteristics" pattern used by many simple/cheap BLE modules, so
// 0xFFF0 is included as the most likely real service UUID; 0xFED6 (primary
// notify) doesn't fit that pattern, so it's also included standalone in
// case the device reuses that UUID as its own service too. connect()
// grants access to all of them, then iterates whichever actually turn out
// to be real primary services on this device and matches by characteristic
// UUID -- the same approach the vendor's own SDK uses, just constrained to
// this pre-declared candidate set. If none of these guesses are the real
// service UUID, connect() fails with a clear, specific error rather than a
// silent wrong-target write/subscribe -- the real fix at that point is
// capturing the actual service UUID from a live BLE scan (e.g. nRF Connect)
// against the physical device.
const CANDIDATE_SERVICE_UUIDS = [
  '0000fff0-0000-1000-8000-00805f9b34fb',
  '0000fed6-0000-1000-8000-00805f9b34fb',
  '0000fff1-0000-1000-8000-00805f9b34fb',
  '0000fff2-0000-1000-8000-00805f9b34fb'
];

const INITIAL_STATE: FetalDopplerState = {
  status: 'idle'
};

@Injectable({
  providedIn: 'root'
})
export class FetalDopplerManagerService {

  private rawBluetoothDevice: any = null;
  private gattServer: any = null;
  private writeCharacteristic: any = null;
  private readonly reassembler = new FetalDopplerFrameReassembler();

  // Tracked so disconnect()/handleDisconnected() can actually remove these
  // listeners instead of just dropping references to them. Web Bluetooth
  // commonly hands back the SAME underlying BluetoothDevice/characteristic
  // wrapper objects across a disconnect->reconnect cycle for a device the
  // user already granted permission for -- without this, a second connect()
  // would add a second set of listeners on top of the first, so a single
  // real notification/disconnect event fires the handler multiple times,
  // feeding duplicate bytes into the one shared reassembler.
  private notifyCharacteristics: any[] = [];
  private readonly boundHandleDisconnected = () => this.handleDisconnected();
  private readonly boundHandleNotification = (event: any) => this.handleNotification(event.target.value as DataView);

  // Serializes writeCommand() calls -- Web Bluetooth allows only one
  // outstanding GATT operation per device at a time. The volume slider
  // fires a write on every ngModelChange tick while dragging, and without
  // this, overlapping writeValueWithResponse() calls are a realistic
  // trigger for "GATT operation already in progress" rejections.
  private writeQueue: Promise<void> = Promise.resolve();

  // Recording audio straight from the device's own SOUND frames (see
  // fetal-doppler-protocol.ts's FetalDopplerAudioDecoder) instead of the
  // browser mic -- captures the doppler's actual audio, not whatever the
  // mic happens to pick up acoustically.
  private readonly audioDecoder = new FetalDopplerAudioDecoder();
  private audioCaptureActive = false;
  private audioChunks: Int16Array[] = [];

  // Diagnostic only -- logs the first time each frame TYPE is seen in a
  // connection, once each, so the console/Logs panel can answer "did any
  // FHR/FHR_LEGACY/FHR2/MHR frame ever arrive at all" without flooding at
  // real frame rate.
  private readonly seenFrameTypes = new Set<string>();

  private readonly stateSubject = new BehaviorSubject<FetalDopplerState>(INITIAL_STATE);
  readonly state$: Observable<FetalDopplerState> = this.stateSubject.asObservable();

  async connect(): Promise<void> {
    const currentStatus = this.stateSubject.value.status;
    if (currentStatus === 'requesting_device' || currentStatus === 'connecting') {
      return;
    }

    this.setState({ ...this.stateSubject.value, status: 'requesting_device', errorMessage: undefined });

    try {
      const bluetooth = (navigator as any).bluetooth;
      // No dedicated service UUID to filter on (see the file-header
      // comment) -- acceptAllDevices is the only option here, same as the
      // vendor SDK's own "iterate everything" approach forces on native.
      const device = await bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: CANDIDATE_SERVICE_UUIDS
      });

      this.rawBluetoothDevice = device;
      const browserDeviceId: string | undefined = device?.id;

      this.setState({ ...this.stateSubject.value, status: 'connecting', browserDeviceId });

      device.addEventListener('gattserverdisconnected', this.boundHandleDisconnected);

      this.gattServer = await device.gatt.connect();

      const services = await this.gattServer.getPrimaryServices();
      let notifyChars: any[] = [];
      let writeChar: any = null;

      for (const service of services) {
        const characteristics = await service.getCharacteristics();
        for (const characteristic of characteristics) {
          const uuid: string = characteristic.uuid;
          if (uuid === NOTIFY_CHARACTERISTIC_UUID_PRIMARY || uuid === NOTIFY_CHARACTERISTIC_UUID_SECONDARY) {
            notifyChars.push(characteristic);
          } else if (uuid === WRITE_CHARACTERISTIC_UUID) {
            writeChar = characteristic;
          }
        }
      }

      if (notifyChars.length === 0) {
        throw new Error(
          'Connected, but neither known notify characteristic (0xFED6 / 0xFFF1) was found on any granted service. ' +
          'The real GATT service UUID this device uses is likely different from the best-effort guesses this app ' +
          'tries (0xFFF0 / 0xFED6 / 0xFFF1 / 0xFFF2) -- capture the actual service UUID from a live BLE scan ' +
          '(e.g. nRF Connect) against the physical device and update CANDIDATE_SERVICE_UUIDS.'
        );
      }

      this.writeCharacteristic = writeChar;
      this.notifyCharacteristics = notifyChars;
      this.reassembler.reset();
      this.seenFrameTypes.clear();

      for (const notifyChar of notifyChars) {
        notifyChar.addEventListener('characteristicvaluechanged', this.boundHandleNotification);
        await notifyChar.startNotifications();
      }

      this.setState({ ...this.stateSubject.value, status: 'connected' });
      console.log('[FetalDopplerManager] Connected --', notifyChars.length, 'notify characteristic(s) subscribed, write characteristic', writeChar ? 'found' : 'NOT found');
      if (writeChar) {
        const props = writeChar.properties ?? {};
        console.log('[FetalDopplerManager] Write characteristic properties: write =', !!props.write, ', writeWithoutResponse =', !!props.writeWithoutResponse);
      }
    } catch (e) {
      console.error('[FetalDopplerManager] Connect flow failed:', e);
      const isCancelled = (e as any)?.name === 'NotFoundError';
      this.setState({
        ...this.stateSubject.value,
        status: isCancelled ? 'idle' : 'error',
        errorMessage: isCancelled ? undefined : String((e as any)?.message ?? e)
      });
    }
  }

  disconnect(): void {
    console.log('[FetalDopplerManager] Disconnect requested by user');
    if (this.gattServer?.connected) {
      this.gattServer.disconnect();
    }
    this.removeListeners();
    this.rawBluetoothDevice = null;
    this.gattServer = null;
    this.writeCharacteristic = null;
    this.reassembler.reset();
    this.seenFrameTypes.clear();
    this.audioCaptureActive = false;
    this.audioChunks = [];
    this.setState({ ...INITIAL_STATE });
  }

  private handleDisconnected(): void {
    console.warn('[FetalDopplerManager] Device disconnected');
    const hadActiveRecording = this.audioCaptureActive && this.audioChunks.length > 0;
    this.removeListeners();
    this.rawBluetoothDevice = null;
    this.gattServer = null;
    this.writeCharacteristic = null;
    this.reassembler.reset();
    this.seenFrameTypes.clear();
    this.audioCaptureActive = false;
    this.audioChunks = [];
    this.setState({
      ...INITIAL_STATE,
      status: 'disconnected',
      errorMessage: hadActiveRecording
        ? 'The device disconnected mid-recording -- the audio captured so far could not be saved.'
        : undefined
    });
  }

  private removeListeners(): void {
    if (this.rawBluetoothDevice) {
      this.rawBluetoothDevice.removeEventListener('gattserverdisconnected', this.boundHandleDisconnected);
    }
    for (const notifyChar of this.notifyCharacteristics) {
      notifyChar.removeEventListener('characteristicvaluechanged', this.boundHandleNotification);
    }
    this.notifyCharacteristics = [];
  }

  // Starts accumulating decoded PCM from this point on -- call right when a
  // reading session starts. stopAudioCapture() returns everything captured
  // since as a playable/downloadable WAV Blob, or null if nothing came in
  // (e.g. the device never sent a SOUND frame during the session).
  startAudioCapture(): void {
    this.audioDecoder.reset();
    this.audioChunks = [];
    this.audioCaptureActive = true;
  }

  stopAudioCapture(): Blob | null {
    this.audioCaptureActive = false;
    if (!this.audioChunks.length) return null;
    const totalLength = this.audioChunks.reduce((sum, c) => sum + c.length, 0);
    const combined = new Int16Array(totalLength);
    let offset = 0;
    for (const chunk of this.audioChunks) {
      combined.set(chunk, offset);
      offset += chunk.length;
    }
    this.audioChunks = [];
    return pcmToWavBlob(combined, FETAL_DOPPLER_AUDIO_SAMPLE_RATE_HZ);
  }

  async setFhrVolume(volume: number): Promise<void> {
    await this.writeCommand(encodeFhrVolumeCommand(volume));
  }

  async setFhrAlarmValue(volume: number): Promise<void> {
    await this.writeCommand(encodeFhrAlarmValueCommand(volume));
  }

  async setFhrAlarmLevel(level: number): Promise<void> {
    await this.writeCommand(encodeFhrAlarmLevelCommand(level));
  }

  async sendTocoReset(isToco: 0 | 1, tocoResetValue: number): Promise<void> {
    const command = encodeTocoResetCommand(isToco, tocoResetValue);
    if (!command) {
      // Matches the vendor SDK's own behavior: isToco=0 sends nothing.
      return;
    }
    await this.writeCommand(command);
  }

  private writeCommand(bytes: Uint8Array): Promise<void> {
    if (!this.writeCharacteristic || !this.gattServer?.connected) {
      return Promise.reject(new Error('Cannot send command -- not connected, or the write characteristic was not found on this device.'));
    }
    const characteristic = this.writeCharacteristic;
    const run = this.writeQueue.then(() => this.writeToCharacteristic(characteristic, bytes));
    // Chain the NEXT write off a settled version of this one regardless of
    // outcome -- a failed write must not permanently jam the queue for
    // every write after it.
    this.writeQueue = run.then(() => undefined, () => undefined);
    return run;
  }

  // writeValueWithResponse() unconditionally fails with "NotSupportedError:
  // GATT operation not permitted" (DOMException code 9) when a
  // characteristic's own advertised `properties` don't include `write`
  // (with-response). The vendor's iOS SDK is documented (see fetal-doppler-
  // protocol.ts's header) as using CBCharacteristicWriteWithResponse, but
  // that's a DIFFERENT API layer -- iOS's write-type constant doesn't have
  // to match what this exact characteristic advertises over GATT to a
  // browser. Reading the characteristic's own properties and picking the
  // write method it actually supports, rather than hardcoding one, is the
  // only way to be correct regardless of which write mode the real
  // hardware exposes.
  private async writeToCharacteristic(characteristic: any, bytes: Uint8Array): Promise<void> {
    const props = characteristic.properties;
    if (props?.write) {
      await characteristic.writeValueWithResponse(bytes);
    } else if (props?.writeWithoutResponse) {
      await characteristic.writeValueWithoutResponse(bytes);
    } else {
      throw new Error(
        `Write characteristic (${characteristic.uuid}) does not advertise either write mode -- cannot send this command to the device.`
      );
    }
  }

  // Feeds every incoming notification's raw bytes into the shared
  // reassembler (both notify characteristics feed the same logical byte
  // stream per the protocol reference) and applies whatever complete
  // frames come out of it to state.
  private handleNotification(value: DataView): void {
    const frames = this.reassembler.push(value);
    for (const frame of frames) {
      if (!this.seenFrameTypes.has(frame.type)) {
        this.seenFrameTypes.add(frame.type);
        console.log('[FetalDopplerManager] First frame of type', frame.type, 'seen this session:', frame.reading ?? '(audio payload)');
      }
      if (frame.reading) {
        this.applyReading(frame.reading);
      }
      if (this.audioCaptureActive && frame.audioPayload) {
        this.audioChunks.push(this.audioDecoder.decode(frame.audioPayload));
      }
    }
  }

  // Each frame type only ever reports a subset of fields (e.g. an MHR frame
  // only carries maternalHeartRate) -- merge into existing state rather
  // than replace it, so a field from an earlier frame isn't wiped out by a
  // later frame of a different type that simply doesn't report it. Doesn't
  // touch `status` -- that's owned by connect()/disconnect()/
  // handleDisconnected() only, not implicitly reset by every data event.
  private applyReading(reading: FetalDopplerReading): void {
    if (reading.fetalHeartRate !== undefined && this.stateSubject.value.fetalHeartRate === undefined) {
      console.log('[FetalDopplerManager] First valid (flagged) FHR value this session:', reading.fetalHeartRate, 'bpm');
    }
    this.setState({ ...this.stateSubject.value, ...reading });
  }

  private setState(state: FetalDopplerState): void {
    this.stateSubject.next(state);
  }
}
